import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { pitchesApi } from "../../repositories/pitches";
import { soundchartsEndpoints } from "../../repositories/soundcharts";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon, PencilIcon } from "@heroicons/react/24/solid";
import { BarChart3, Music, Image as ImageIcon } from "lucide-react";
import { downloadAppleCsv, downloadDeezerCsv } from "../../utils/pitchCsvDownload";
import Swal from "sweetalert2";
import { useAppSelector } from "../../hooks/redux";
import PageHeader from "../../components/ui/PageHeader";
import Button from "../../components/ui/Button";
import { buttonVariants } from "../../components/ui/buttonVariants";
import { BreadcrumbItem, HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import { Skeleton, SkeletonCard, SkeletonFields } from "../../components/ui/skeleton";
import StatCard from "../../components/ui/StatCard";
import StatusBadge from "../../components/ui/StatusBadge";
import DetailField from "../../components/ui/DetailField";

interface Contributor {
  id: number;
  name: string;
  role: string[];
  pitchId: number;
  createdAt: string;
  updatedAt: string;
}

interface PitchData {
  id: string | number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  focusTrackName?: string;
  trackISRC: string;
  songDescription: string;
  mainGenre: string;
  songMood: string;
  language: string;
  featuredArtists: string;
  shortDescription: string;
  expandedDescription?: string;
  longDescription: string;
  lyrics: string;
  linkToListen: string;
  promoPhotos: string;
  status: string;
  companyName?: string;
  companyType?: string;
  contributors?: Contributor[];
  AmazonInfo?: Record<string, any>;
  SpotifyInfo?: Record<string, any>;
  DeezerInfo?: Record<string, any>;
  TidalInfo?: Record<string, any>;
  AudiomackInfo?: Record<string, any>;
  YouTubeInfo?: Record<string, any>;
  SoundCloudInfo?: Record<string, any>;
  AppleInfo?: Record<string, any>;
  SiriusXMInfo?: Record<string, any>;
  VevoInfo?: Record<string, any>;
  platformSpecificUpcs?: Array<{ dsp?: string; dspName?: string; upc: string; notes?: string }>;
}

// Shared accordion chrome, so Contributors / Lyrics / each DSP look alike.
const disclosureButtonClass =
  "flex items-center justify-between w-full gap-3 px-4 py-3 text-sm font-semibold text-left text-black transition-colors bg-white border rounded-lg border-neutral-200 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700";
const disclosurePanelClass =
  "px-4 py-4 mt-1 bg-white border rounded-lg border-neutral-100";
const chevronClass = (open: boolean) =>
  `${open ? "" : "transform rotate-180"} w-4 h-4 text-neutral-500 transition-transform shrink-0`;

/** Secondary-button styling for the links on this page (Listen Here, Promo Photos). */
const linkButtonClass = buttonVariants({ variant: "secondary" });

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="p-4 bg-white border rounded-lg border-neutral-200 sm:p-5">
    <h3 className="mb-4 text-xs font-semibold tracking-wide uppercase text-primary-900">
      {title}
    </h3>
    {children}
  </section>
);

const ViewSubmission: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [data, setData] = useState<PitchData | null>(null);
  const [songPopularity, setSongPopularity] = useState<number | null>(null);
  const [artistPopularity, setArtistPopularity] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  // This page opens from the dashboard (?from=dashboard) or, by default, from past submissions.
  // The breadcrumb trail reflects that origin.
  const openerParams = new URLSearchParams(window.location.search);
  const fromDashboard = openerParams.get('from') === 'dashboard';
  // Past Submissions passes the page it was on (?page=7) so returning lands on
  // that page rather than resetting the user to the top of the list.
  const returnPage = openerParams.get('page');
  const pastSubmissionsTo = `/past-submissions${
    returnPage ? `?page=${encodeURIComponent(returnPage)}` : ''
  }`;
  const breadcrumbItems: BreadcrumbItem[] = fromDashboard
    ? [
        HOME_CRUMB,
        { label: 'Review Pitch' },
      ]
    : [
        HOME_CRUMB,
        { label: 'Past Submissions', to: pastSubmissionsTo },
        { label: 'Review Pitch' },
      ];

  useEffect(() => {
    const fetchPitchData = async () => {
      try {
        const { data } = await pitchesApi.getOne(Number(id));
        if (!data.success) throw new Error("Failed to fetch data");
        setData(data.data);

        // Fetch popularity scores using ISRC
        if (data.data.trackISRC) {
          try {
            // Get song UUID from ISRC
            const songResponse = await soundchartsEndpoints.getSongByISRC(data.data.trackISRC);
            const songUuid = songResponse.data.object?.uuid;
            const artistUuid = songResponse.data.object?.artists?.[0]?.uuid;
            if (songUuid) {
              const songPopResponse = await soundchartsEndpoints.getPopularityBySong(songUuid, "spotify");
              let latestValue = songPopResponse?.data?.items?.[0]
              let array: number[] = []
              if (latestValue?.plots) {
                latestValue?.plots.forEach((plot: any) => {
                  array.push(plot.value);
                  setSongPopularity(Math.max(...array));

                })

              } else {
                setSongPopularity(latestValue?.value);
              }
              // setSongPopularity(songPopResponse?.data?.items?.[0]?.plots?.[0]?.value || null);
            }
            if (artistUuid) {
              const today = new Date();
              const previousDate = new Date(today);
              previousDate.setDate(today.getDate() - 1)
              const previousFormattedDate = previousDate.toISOString().split('T')[0];
              const artistPopResponse = await soundchartsEndpoints.getPopularityByArtist(artistUuid, "spotify", `startDate=${previousFormattedDate}`);
              let latestValue = artistPopResponse?.data?.items?.[0]
              let array: number[] = []
              if (latestValue?.plots) {
                latestValue?.plots.forEach((plot: any) => {
                  array.push(plot.value);
                  setArtistPopularity(Math.max(...array));

                })

              } else {
                setArtistPopularity(latestValue?.value);
              }
            }
          }
          catch (error) {
            console.error("Failed to fetch popularity scores:", error);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPitchData();
  }, [id]);

  if (loading || !data) {
    return (
      <div className="py-2 mx-auto sm:p-6 max-w-7xl" aria-busy="true">
        <Skeleton className="w-20 h-4 mb-4" />
        <Skeleton className="h-8 mb-2 w-72" />
        <Skeleton className="w-48 h-4 mb-6" />
        <SkeletonCard lines={2} className="mb-6" />
        <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200 sm:p-6">
          <Skeleton className="w-40 h-5 mb-4" />
          <SkeletonFields count={9} />
        </div>
        <SkeletonCard lines={4} />
      </div>
    );
  }

  const excludedFields = ["id", "pitchId", "updatedAt", "platformSpecificUpcs"];

  // DSP configuration for marketing notes
  const dspConfig = [
    { key: 'SpotifyInfo', label: 'Spotify' },
    { key: 'AppleInfo', label: 'Apple Music' },
    { key: 'AmazonInfo', label: 'Amazon Music' },
    { key: 'TidalInfo', label: 'Tidal' },
    { key: 'DeezerInfo', label: 'Deezer' },
    { key: 'AudiomackInfo', label: 'Audiomack' },
    { key: 'YouTubeInfo', label: 'YouTube' },
    { key: 'SoundCloudInfo', label: 'SoundCloud' },
    { key: 'SiriusXMInfo', label: 'SiriusXM' },
  ] as const;

  // Get all DSPs that have marketing notes
  const marketingNotes = dspConfig
    .map(({ key, label }) => ({
      label,
      note: data[key]?.marketingNote
    }))
    .filter(item => item.note);
  const isAdmin = user?.role === "admin";

  return (
    <div className="py-2 mx-auto sm:p-6 max-w-7xl">
      <PageHeader
        breadcrumbs={breadcrumbItems}
        className="mb-6"
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>
              {data.artistName} - {data.releaseTitle}
            </span>
            <StatusBadge status={data.status} isAdmin={isAdmin} />
          </span>
        }
        actions={
          // The Edit link passes the originating list page on, so
          // Edit -> back -> back returns to it rather than to page 1.
          data.status !== "delivered" ? (
            <Button asChild variant="secondary">
              <Link
                to={`/pitch/edit/${data.id}?from=review${
                  returnPage ? `&page=${encodeURIComponent(returnPage)}` : ''
                }`}
              >
                <PencilIcon className="w-4 h-4" /> Edit Pitch
              </Link>
            </Button>
          ) : undefined
        }
      />

      {/* Popularity scores - real Soundcharts metrics already fetched above. */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2">
        <StatCard
          label="Artist Popularity"
          value={artistPopularity !== null ? artistPopularity : "Not Available"}
          icon={<BarChart3 className="w-5 h-5" />}
        />
        <StatCard
          label="Song Popularity"
          value={songPopularity !== null ? songPopularity : "Not Available"}
          icon={<BarChart3 className="w-5 h-5" />}
        />
      </div>

      <div className="space-y-4">
        <Section title="Release Details">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Release Date" value={data.releaseDate} />
            <DetailField label="Configuration" value={data.configuration} />
            <DetailField label="UPC" value={data.releaseUPC} />
            {(data.configuration?.includes("EP") || data.configuration?.includes("Album")) && data.focusTrackName && (
              <DetailField label="Focus Track" value={data.focusTrackName} />
            )}
            <DetailField label="ISRC" value={data.trackISRC} />
            <DetailField label="Main Genre" value={data.mainGenre} />
            <DetailField label="Song Mood" value={data.songMood} />
            <DetailField label="Language" value={data.language} />
            <DetailField label="Featured Artists" value={data.featuredArtists} />
            <DetailField label="Song Description" value={data.songDescription} />
          </dl>
        </Section>

        <Section title="Description">
          <dl className="space-y-4">
            <DetailField label="Description" value={data.shortDescription} />
            {/* Only show if any marketing notes exist */}
            {marketingNotes.length > 0 && (
              <div className="min-w-0">
                <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                  DSP-Specific Marketing Information
                </dt>
                <dd className="mt-1">
                  <ul className="space-y-1 text-sm text-black">
                    {marketingNotes.map(({ label, note }) => (
                      <li key={label} className="break-words">
                        <span className="font-medium text-primary-900">{label}:</span> {note}
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
            <DetailField label="Marketing Drivers" value={data.longDescription} />
            {data.expandedDescription && (
              <DetailField label="Expanded Description" value={data.expandedDescription} />
            )}
          </dl>
        </Section>

        {(data.linkToListen || data.promoPhotos) && (
          <div className="flex flex-wrap gap-3">
            {data.linkToListen && (
              <a
                href={data.linkToListen}
                className={linkButtonClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Music className="w-4 h-4" /> Listen Here
              </a>
            )}
            {data.promoPhotos && (
              <a
                href={data.promoPhotos}
                className={linkButtonClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ImageIcon className="w-4 h-4" /> Promo Photos
              </a>
            )}
          </div>
        )}

        {data?.contributors && data.contributors.length > 0 && (
          <Disclosure as="div">
            {({ open }) => (
              <>
                <Disclosure.Button className={disclosureButtonClass}>
                  Contributors
                  <ChevronUpIcon className={chevronClass(open)} />
                </Disclosure.Button>
                <Disclosure.Panel className={disclosurePanelClass}>
                  <ul className="space-y-2">
                    {data.contributors?.map((contributor) => (
                      <li
                        key={contributor.id}
                        className="px-3 py-2 border rounded-lg border-neutral-100 bg-primary-50/40"
                      >
                        <p className="text-sm font-medium text-black">{contributor.name}</p>
                        <p className="mt-0.5 text-xs text-neutral-600">
                          <span className="font-medium">Role:</span> {contributor.role.join(", ")}
                        </p>
                      </li>
                    ))}
                  </ul>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        )}

        {data.lyrics && data.lyrics.trim().length > 0 && (
          <Disclosure as="div">
            {({ open }) => (
              <>
                <Disclosure.Button className={disclosureButtonClass}>
                  Lyrics
                  <ChevronUpIcon className={chevronClass(open)} />
                </Disclosure.Button>
                <Disclosure.Panel className={disclosurePanelClass}>
                  <pre className="font-sans text-sm break-words whitespace-pre-wrap text-neutral-700">
                    {data.lyrics}
                  </pre>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        )}

        <h3 className="pt-2 text-xs font-semibold tracking-wide uppercase text-primary-900">
          All DSPs
        </h3>

        {Object.entries(data).map(
          ([key, value]) =>
            key.endsWith("Info") &&
            value && (
              <Disclosure key={key} as="div">
                {({ open }) => (
                  <>
                    <Disclosure.Button className={disclosureButtonClass}>
                      {formatKey(key)}

                      <div className="flex items-center gap-3">
                        {(key === "AppleInfo" || key === "DeezerInfo") &&
                          (data.status === "submitted" || data.status === "delivered") && (
                            <Button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const success = key === "AppleInfo"
                                  ? downloadAppleCsv(data)
                                  : downloadDeezerCsv(data, user?.labelName || "");
                                if (!success) {
                                  Swal.fire({
                                    title: "Download Blocked",
                                    text: "Download blocked. Please disable ad-blockers or browser extensions that may prevent file downloads, then try again.",
                                    icon: "warning",
                                    confirmButtonText: "Retry Download",
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      key === "AppleInfo"
                                        ? downloadAppleCsv(data)
                                        : downloadDeezerCsv(data, user?.labelName || "");
                                    }
                                  });
                                }
                              }}
                              variant="link"
                              size="sm"
                            >
                              Download CSV Pitch
                            </Button>
                          )}
                        <ChevronUpIcon className={chevronClass(open)} />
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel className={disclosurePanelClass}>
                      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(value as Record<string, any>)
                          .filter(
                            ([subKey, val]) =>
                              !excludedFields.includes(subKey) &&
                              val !== "" &&
                              val !== null &&
                              val !== undefined
                          )
                          .map(([subKey, val]) => {
                            // Render platform-specific UPCs as formatted list
                            if (subKey === "platformSpecificUpcs" && Array.isArray(val) && val.length > 0) {
                              return val.map((upcEntry: any, idx: number) => (
                                <DetailField
                                  key={`${subKey}-${idx}`}
                                  label="UPC"
                                  value={`${upcEntry.upc}${upcEntry.notes ? ` - ${upcEntry.notes}` : ""}`}
                                />
                              ));
                            }
                            return (
                              <DetailField
                                key={subKey}
                                label={formatKey(subKey)}
                                value={
                                  subKey === "createdAt"
                                    ? new Date(val).toLocaleDateString()
                                    : String(val)
                                }
                              />
                            );
                          })}
                      </dl>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )
        )}
      </div>
    </div>
  );
};

const formatKey = (key: string) => {
  if (key === "songDetails") return "Release Stage";
  if (key === "SoundCloudInfo") return "Soundcloud Info";
  if (key === "SiriusXMInfo") return "SiriusXM Info";
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
};
export default ViewSubmission;
